/**
 * This module provides a WebSocket client implementation for connecting to AppSync Events API.
 * It handles connection establishment, channel subscription, and message processing.
 */

import { AppSyncMessage } from "./ws-types";
import { handleWebSocketMessage } from "./ws-handlers";
import * as Sentry from "@sentry/browser";

// Types
type WebSocketDetails = {
  ws: string;
  http: string;
  token: string;
};

type AuthorizationHeaders = {
  host: string;
  Authorization: string;
};

// Connection state
let websocket: WebSocket | null = null;
let isConnectionAcked = false;

// Add reconnection control
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 3;
let isReconnecting = false;

/**
 * Encodes an object into a base64 URL-safe string.
 * Used for preparing WebSocket subprotocol headers.
 *
 * @param obj - Object containing key-value pairs to encode
 * @returns Base64 URL-safe encoded string
 */
function base64UrlEncode(obj: Record<string, string>): string {
  return btoa(JSON.stringify(obj))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Retrieves WebSocket connection details from the backend.
 *
 * @throws {Error} If the server request fails or authentication is invalid.
 * @returns Promise containing WebSocket URL, HTTP endpoint, and authentication token.
 */
async function getWebSocketDetails(): Promise<WebSocketDetails | undefined> {
  try {
    const response = await fetch('/websockets');

    if (response.status === 401) {
      console.log("[WebSocket] Session expired. Redirecting to login.");
      window.location.href = "/login";
    }

    if (response.status === 403) {
      console.log("[WebSocket] User not logged in.");
      return;
    }

    if (!response.ok) {
      console.error("[WebSocket] Failed to get details:", {
        status: response.status,
        statusText: response.statusText
      });
      throw new Error(`Failed to get WebSocket details: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("[WebSocket] Error getting details:", error);
    throw error;
  }
}

/**
 * Parses a JWT token and extracts its payload.
 *
 * @param token - JWT token string
 * @returns Decoded JWT payload as an object
 */
function parseJwt(token: string): Record<string, any> {
  const [, payloadBase64] = token.split('.');
  const payloadJson = atob(payloadBase64.replace(/_/g, '/').replace(/-/g, '+'));
  return JSON.parse(payloadJson);
}

/**
 * Subscribes to a specified channel after WebSocket connection is established.
 *
 * @param channelName - Name of the channel to subscribe to
 * @param authorization - Authorization headers for the subscription
 * @throws {Error} If WebSocket is not connected or not acknowledged
 */
function subscribeToChannel(channelName: string, authorization: AuthorizationHeaders): void {
  if (!websocket || !isConnectionAcked) {
    console.warn("WebSocket not open or not acknowledged yet.");
    return;
  }

  const subscriptionId = "sub-" + Math.random().toString(36).slice(2, 7);
  const subscribeMessage = {
    type: "subscribe",
    id: subscriptionId,
    channel: channelName,
    authorization,
  };

  console.log(`[WebSocket] Subscribing to channel: ${channelName}, id=${subscriptionId}`);
  websocket.send(JSON.stringify(subscribeMessage));
}

/**
 * Establishes a WebSocket connection to AppSync Events API and sets up message handlers.
 * Automatically subscribes to a user-specific channel upon successful connection.
 *
 * @throws {Error} If WebSocket details are invalid or connection fails
 */
export async function connectToChannel(): Promise<void> {
  // Check if user is authenticated before attempting connection
  const wsElement = document.querySelector('[data-ws-enabled]');
  const isWebSocketEnabled = wsElement?.getAttribute('data-ws-enabled') === 'true';

  if (!isWebSocketEnabled) {
    console.log("[WebSocket] Connection skipped: User not authenticated");
    return;
  }

  // Disable WebSocket connection for disabled connections
  const shouldDisableConnection = document.querySelector('[data-disable-connection="true"]') !== null;

  if (shouldDisableConnection) {
    console.log("[WebSocket] Connection disabled:", "Disabled connection");
    return;
  }

  const connection = await getWebSocketDetails();
  if (!connection) {
    return;
  }

  const { ws, http, token } = connection;

  if (!ws || !http || !token) {
    throw new Error('Invalid WebSocket details');
  }

  // Configure authorization headers
  const authorizationHeaders: AuthorizationHeaders = {
    host: http,
    Authorization: `Bearer ${token}`,
  };

  // Debug header
  const headerString = `header-${base64UrlEncode(authorizationHeaders)}`;

  // Initialize WebSocket connection
  websocket = new WebSocket(ws, [headerString, "aws-appsync-event-ws"]);

  // Set up event handlers
  websocket.onopen = () => {
    console.log("[WebSocket] onopen -> sending connection_init");
    websocket?.send(JSON.stringify({ type: "connection_init" }));
  };

  // Configure message handler
  websocket.onmessage = (event) => {
    const data: AppSyncMessage = JSON.parse(event.data);

    switch (data.type) {
      case "connection_ack":
        console.log("[WebSocket] Received connection_ack");
        isConnectionAcked = true;
        reconnectAttempts = 0;

        const claims = parseJwt(token);
        const userSub = claims.sub.split('|').join('-');
        const dynamicChannelPath = `/sub/${userSub}`;
        subscribeToChannel(dynamicChannelPath, authorizationHeaders);
        break;

      case "ka":
        // Keep alive - no action needed
        break;

      case "subscribe_success":
        console.log(`[WebSocket] Subscribe success, id=${data.id}`);
        break;

      case "data":
        handleWebSocketMessage(data);
        break;

      case "connection_error":
      case "subscribe_error":
        console.error(`[WebSocket] ${data.type}:`, data.errors);
        if (data.errors?.[0]?.errorType === "UnauthorizedException") {
          handleTokenRefresh();
        } else {
          Sentry.captureMessage(`WebSocket ${data.type}`, {
            level: "error",
            extra: { errors: data.errors }
          });
        }

        break;

      default:
        console.log("[WebSocket] Unknown message type:", data);
        break;
    }
  };

  websocket.onerror = (event) => {
    console.error("[WebSocket] Error:", event);
    Sentry.captureException(new Error("WebSocket connection error"), {
      extra: {
        readyState: websocket?.readyState,
        url: websocket?.url
      }
    });
  };

  websocket.onclose = (evt) => {
    websocket = null;
    isConnectionAcked = false;

    // Attempt reconnection if closed unexpectedly (e.g., if no reason is provided)
    if (!isReconnecting && (!evt.reason || evt.reason === "No reason")) {
      console.log("[WebSocket] Connection closed unexpectedly, attempting to reconnect...");
      handleTokenRefresh();
    } else {
      console.log("[WebSocket] Closed:", evt.reason || "No reason");
    }
  };
}

/**
 * Gracefully terminates the WebSocket connection.
 */
export function closeChannel(): void {
  if (websocket) {
    console.log("[WebSocket] Closing...");
    websocket.close();
    websocket = null;
    isConnectionAcked = false;
  }
}

// Modify the token refresh handler
export function handleTokenRefresh(): void {
  if (isReconnecting) {
    console.log("[WebSocket] Already attempting to reconnect");
    return;
  }

  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.error("[WebSocket] Max reconnection attempts reached");
    Sentry.captureMessage("WebSocket max reconnection attempts reached", {
      level: "error",
      extra: {
        attempts: reconnectAttempts,
        maxAttempts: MAX_RECONNECT_ATTEMPTS
      }
    });
    return;
  }

  isReconnecting = true;
  reconnectAttempts++;

  console.log(`[WebSocket] Attempting reconnect ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}`);

  // Close existing connection
  closeChannel();

  // Add delay before reconnecting
  setTimeout(() => {
    connectToChannel()
      .catch(error => {
        console.error("[WebSocket] Reconnection failed:", error);
      })
      .finally(() => {
        isReconnecting = false;
      });
  }, 1000 * reconnectAttempts); // Exponential backoff
}
